import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { schemaService } from '../../services';
import { ExtractionSchema, Domain } from '../../types';
import { Modal } from '../ui';

interface Props {
  schema: ExtractionSchema | null;
  domains: Domain[];
  isDuplicate?: boolean;
  onClose: () => void;
  onSave: () => void;
}

export const SchemaFormModal: React.FC<Props> = ({ schema, domains, isDuplicate = false, onClose, onSave }) => {
  const isEdit = !!schema && !isDuplicate;
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    domain: schema?.domain || (domains[0]?.name || ''),
    version: isDuplicate ? '' : (schema?.version || ''),
    name: isDuplicate ? `${schema?.name || ''} (copie)` : (schema?.name || ''),
    field_definitions: schema?.field_definitions ? JSON.stringify(schema.field_definitions, null, 2) : '{}',
    description: schema?.description || ''
  });
  const [saving, setSaving] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [error, setError] = useState('');

  const copyToClipboard = () => {
    navigator.clipboard.writeText(formData.field_definitions);
  };

  const exportToJson = () => {
    const blob = new Blob([formData.field_definitions], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filename = schema
      ? `${schema.domain}-${schema.version}-field-definitions.json`
      : `${formData.domain}-${formData.version}-field-definitions.json`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const parseFieldDefinitions = (): Record<string, unknown> | null => {
    try {
      return JSON.parse(formData.field_definitions);
    } catch {
      setError('JSON invalide');
      return null;
    }
  };

  const handleCreateAndBootstrap = async () => {
    if (isEdit) return;
    const parsedFieldDefinitions = parseFieldDefinitions();
    if (!parsedFieldDefinitions) return;

    setBootstrapping(true);
    setError('');
    try {
      const newSchema = await schemaService.create({
        domain: formData.domain,
        version: formData.version,
        name: formData.name,
        schema_json: {},
        field_definitions: parsedFieldDefinitions,
        description: formData.description || undefined
      });
      await schemaService.bootstrapFromGT(newSchema.id);
      navigate(`/parametres/schemas/${newSchema.id}/edit`);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Erreur lors de la création');
    } finally {
      setBootstrapping(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const parsedFieldDefinitions = parseFieldDefinitions();
      if (!parsedFieldDefinitions) {
        setSaving(false);
        return;
      }

      if (isEdit) {
        await schemaService.update(schema.id, {
          name: formData.name,
          field_definitions: parsedFieldDefinitions,
          description: formData.description || undefined
        });
      } else {
        await schemaService.create({
          domain: formData.domain,
          version: formData.version,
          name: formData.name,
          schema_json: {}, // Will be auto-generated from field_definitions on backend
          field_definitions: parsedFieldDefinitions,
          description: formData.description || undefined
        });
      }
      onSave();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEdit ? 'Modifier le Schema' : (isDuplicate ? 'Dupliquer le Schema' : 'Nouveau Schema')}
      size="lg"
    >
      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>
      )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Domaine</label>
              <select
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                disabled={isEdit}
                className="w-full border rounded px-3 py-2"
                required
              >
                {domains.map((d, index) => (
                  <option key={`${d.name}-${index}`} value={d.name}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Version</label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="ex: 1.0, 1.1, 2.0"
                required
              />
              {isEdit && (
                <p className="text-xs text-yellow-600 mt-1">
                  ⚠️ Modifier la version peut causer des conflits. Assurez-vous qu'aucune autre version identique n'existe.
                </p>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Nom</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>

          <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium">Field Definitions JSON</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={copyToClipboard}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  📋 Copier
                </button>
                <button
                  type="button"
                  onClick={exportToJson}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  📥 Exporter JSON
                </button>
              </div>
            </div>
            <textarea
              value={formData.field_definitions}
              onChange={(e) => setFormData({ ...formData, field_definitions: e.target.value })}
              className="w-full border rounded px-3 py-2 font-mono text-sm"
              rows={15}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Format: JSON valide avec sections, fields, etc. Le schema_json sera généré automatiquement.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border rounded px-3 py-2"
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-100"
            >
              Annuler
            </button>
            {!isEdit && (
              <button
                type="button"
                onClick={handleCreateAndBootstrap}
                disabled={saving || bootstrapping}
                className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
              >
                {bootstrapping ? 'Création + Bootstrap...' : '🔨 Créer + Construire depuis GT'}
              </button>
            )}
            <button
              type="submit"
              disabled={saving || bootstrapping}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
            >
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </form>
    </Modal>
  );
};
